import { mdiCheckboxBlankCircle, mdiCheckCircle } from '@mdi/js';
import copy from 'copy-text-to-clipboard';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import {
    ProviderClaimEligibility,
    ProviderPayer,
    ProviderPayerSubscriber,
    ProviderSubscriber,
    ProviderSubscriberPatient,
    SupportClaimEligibility,
    SupportPayer,
    SupportPayerSubscriber,
    SupportSubscriber,
    SupportSubscriberPatient
} from '../../../api';
import { ToastContext } from '../../../services';
import { Emoji, LoadingMeter } from '../../base';
import { ActionItem, CardAction } from '../cards/cards';
import * as S from './insurance-overview.styles';

interface IInsuranceOverviewProps {
    loading: boolean;
    eligibilities?: Array<ProviderClaimEligibility | SupportClaimEligibility>;
    payers?: Array<ProviderPayer | SupportPayer>;
    thirdPartyAdministrators?: Array<ProviderPayer | SupportPayer>;
    payerSubscribers?: Array<ProviderPayerSubscriber | SupportPayerSubscriber>;
    subscribers?: Array<ProviderSubscriber | SupportSubscriber>;
    subscriberPatients?: Array<ProviderSubscriberPatient | SupportSubscriberPatient>;

    onEditPayer?: (payer: SupportPayer | ProviderPayer) => void;
    onEditSubscriber?: (subscriber: SupportSubscriber | ProviderSubscriber) => void;
    onEditPayerSubscriber?: (payerSubscriber: SupportPayerSubscriber | ProviderPayerSubscriber) => void;
    onSwapSelfPay?: () => void;
}

export const InsuranceOverview = ({
                                      loading,
                                      payers,
                                      thirdPartyAdministrators,
                                      payerSubscribers,
                                      subscribers,
                                      subscriberPatients,
                                      eligibilities,
                                      onEditPayer,
                                      onEditSubscriber,
                                      onEditPayerSubscriber,
                                      onSwapSelfPay
                                  }: IInsuranceOverviewProps) => {
    const [activeCard, setActiveCard] = useState(0);

    const toastContext = useContext(ToastContext);

    // get payerSubscriber for currently active card (Primary / Secondary / Tertiary)
    const payerSubscriber = _.get(payerSubscribers, activeCard);

    const payer = _.find(payers, { id: _.get(payerSubscriber, 'payer.id') });
    const subscriber = _.find(subscribers, { id: _.get(payerSubscriber, 'subscriber.id') });
    const subscriberPatient = _.find(subscriberPatients, {
        id: _.get(subscriber, 'id'),
    });

    const thirdPartyAdministrator =
        thirdPartyAdministrators && thirdPartyAdministrators.length > activeCard
            ? thirdPartyAdministrators[activeCard]
            : undefined;

    const renderInsuranceActions = () => {
        if (!payer) {
            return [];
        }

        const path = `/claims/by-payer/${payer.id}`;

        if (!onEditPayer) {
            return renderActions([]);
        }

        return renderActions(
            _.compact([
                onEditPayer
                    ? {
                        label: 'Edit Payer',
                        icon: <Emoji type="pencil" title="Edit Payer" />,
                        onClick: () => onEditPayer(payer),
                        divide: true,
                    }
                    : undefined,
                onEditSubscriber && subscriber ? {
                        label: 'Edit Subscriber',
                        icon: <Emoji type="pencil" title="Edit Subscriber" />,
                        onClick: () => onEditSubscriber(subscriber),
                    }
                    : undefined,
                onEditPayerSubscriber && payerSubscriber ? {
                        label: 'Edit Payer Subscriber',
                        icon: <Emoji type="pencil" title="Edit Payer Subscriber" />,
                        onClick: () => onEditPayerSubscriber(payerSubscriber),
                    }
                    : undefined,
                onSwapSelfPay && payerSubscriber ? {
                        label: 'Swap Self-Pay',
                        icon: <Emoji type="repeat" title="Swap Self-Pay" />,
                        onClick: () => onSwapSelfPay(),
                    }
                    : undefined
            ]),
        );
    };

    const renderActions = (actions: CardAction[]) => {
        return actions.map((action, i) => (
            <React.Fragment key={i}>
                <ActionItem action={action} />
                {!!action.divide && <S.Divider />}
            </React.Fragment>
        ));
    };

    const handleTabClick = (index: number): (() => void) => {
        return (): void => {
            setActiveCard(index);
        };
    };

    const renderTabs = (): React.ReactElement => {
        const labels = ['Primary', 'Secondary', 'Tertiary'];
        let tabs: React.ReactElement[];
        if (payerSubscribers && payerSubscribers.length) {
            tabs = _.times(
                Math.min(3, payerSubscribers.length),
                (i: number): React.ReactElement => {
                    const payerSubscriber = payerSubscribers[i];
                    // find entities matching current payerSubscriber
                    const eligibilityByGroupId = _.find(eligibilities, {
                        groupId: _.get(payerSubscriber, 'groupId'),
                    });
                    const eligibilityByPayerId = _.find(eligibilities, {
                        payerId: _.get(payerSubscriber, 'payer.id'),
                    });
                    const eligibilityFound = eligibilityByGroupId || eligibilityByPayerId;
                    const message = eligibilityFound ? 'Eligibility found' : 'No eligibility run';
                    return (
                        <S.Tab key={labels[i]} onClick={handleTabClick(i)} active={activeCard === i}>
                            <S.TabLabel>{labels[i]}</S.TabLabel>
                            <S.TabTooltip direction="top" variant="dark" title={<span>{message}</span>}>
                                {eligibilityFound ? (
                                    <S.ActiveIcon path={mdiCheckCircle} />
                                ) : (
                                    <S.TabIcon path={mdiCheckboxBlankCircle} />
                                )}
                            </S.TabTooltip>
                        </S.Tab>
                    );
                },
            );
        } else {
            tabs = [
                <S.Tab key={labels[0]} active={true}>
                    {labels[0]}
                </S.Tab>,
            ];
        }
        return <S.Tabs>{tabs}</S.Tabs>;
    };

    const renderLoader = (): React.ReactElement => (
        <S.Loader>
            <LoadingMeter />
        </S.Loader>
    );

    const renderCard = (): React.ReactElement | undefined => {
        return (
            <S.StyledCard
                payer={payer}
                thirdPartyAdministrator={thirdPartyAdministrator}
                payerSubscriber={payerSubscriber}
                subscriber={subscriber}
                subscriberPatient={subscriberPatient}
                onEditPayer={onEditPayer}
            />
        );
    };

    return (
        <S.InsuranceOverviewCard size="large">
            <S.Container>
                <S.Content>
                    <S.Header>{renderTabs()}</S.Header>
                    {loading && renderLoader()}
                    {!loading && renderCard()}
                </S.Content>
                {!loading && onEditPayer && <S.Actions>{renderInsuranceActions()}</S.Actions>}
            </S.Container>
        </S.InsuranceOverviewCard>
    );
};
